import { useState, useRef, useEffect } from 'react';

const useSentenceManager = ({ module, subModule, sentenceCount, onMenuDataLoadStart, onMenuDataLoadEnd, id }) => {
  const [sentences, setSentences] = useState([]);
  const [error, setError] = useState(null);
  const [menuData, setMenuData] = useState({});
  const displayedSentences = useRef({});
  const isSequenced = useRef(false);

  useEffect(() => {
    fetchMenuData();
  }, []);

  const fetchMenuData = async () => {
    const API_URL = 'https://script.google.com/macros/s/AKfycbzy-ZmUUNnMXPJR_gZnSlH8hZlxSeN2yxiMSikI49e4gcnPTiHgd9LA_wHLQRZyuvXmWQ/exec';

    try {
      setError(null);
      if (onMenuDataLoadStart) {
        onMenuDataLoadStart();
      }
      const response = await fetch(`${API_URL}?menu=true&id=${id}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setMenuData(data);
    } catch (error) {
      console.error('Error fetching menu data:', error);
      setError(error.message || 'An error occurred while fetching menu data');
    } finally {
      if (onMenuDataLoadEnd) {
        onMenuDataLoadEnd();
      }
    }
  };

  const fetchSentences = async () => {
    const API_URL =
      'https://script.google.com/macros/s/AKfycbzy-ZmUUNnMXPJR_gZnSlH8hZlxSeN2yxiMSikI49e4gcnPTiHgd9LA_wHLQRZyuvXmWQ/exec';

    try {
      setError(null);
      const response = await fetch(
        `${API_URL}?module=${module}&subModule=${subModule}&id=${id}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      if (data && Array.isArray(data.sentences) && data.sentences.length > 0) {
        const rawSentences = data.sentences.map((sentence) =>
          Array.isArray(sentence) ? sentence : [sentence]
        );

        const firstSentence = rawSentences[0][0];
        isSequenced.current = false;
        let processedSentences;
        if (firstSentence.includes('&&')) {
          processedSentences = processSentencesWithAmpersands(rawSentences);
        } else {
          processedSentences = processSentencesWithoutAmpersands(
            rawSentences,
            data.isRandom
          );
        }

        const selectedSentences = selectSentences(
          processedSentences,
          sentenceCount,
          module,
          subModule
        );
        setSentences(selectedSentences);
      } else {
        throw new Error('予期しないエラー発生: データが見つかりませんでした');
      }
    } catch (error) {
      console.error('Error fetching sentences:', error);
      setSentences([]);
      setError(error.message || 'An error occurred while fetching sentences');
      throw error;
    }
  };

  const processSentencesWithoutAmpersands = (rawSentences, isRandom) => {
    const processedSentences = rawSentences.map((sentenceGroup) => {
      if (sentenceGroup[0].includes('##')) {
        isSequenced.current = true;
        return sentenceGroup[0].split('##').map((s) => [s.trim()]);
      }
      return sentenceGroup;
    });

    if (isRandom) {
      shuffleArray(processedSentences);
    }

    return processedSentences;
  };

  const processSentencesWithAmpersands = (rawSentences) => {
    const processedSentences = [];

    for (const rawSentence of rawSentences) {
      const columnedElements = rawSentence[0].split('&&');

      const subElements = columnedElements.map((element) =>
        element.includes('$$') ? element.split('$$') : [element]
      );

      const generateCombinations = (current, index) => {
        if (index === subElements.length) {
          processedSentences.push(current);
          return;
        }

        for (const element of subElements[index]) {
          generateCombinations([...current, element], index + 1);
        }
      };

      generateCombinations([], 0);
    }

    return processedSentences;
  };

  const selectSentences = (
    processedSentences,
    count,
    currentModule,
    currentSubModule
  ) => {
    const key = `${currentModule}:${currentSubModule}`;
    if (!displayedSentences.current[key]) {
      displayedSentences.current[key] = [];
    }

    const selected = [];
    let available = processedSentences.filter(
      (sentence) =>
        !displayedSentences.current[key].some(
          (displayedSentence) =>
            JSON.stringify(displayedSentence) === JSON.stringify(sentence)
        )
    );

    while (selected.length < count) {
      if (available.length === 0) {
        // If we've used all sentences for this module and sub-module, reset only for this combination
        displayedSentences.current[key] = [];
        available = [...processedSentences];
      }

      const randomIndex = Math.floor(Math.random() * available.length);
      const selectedSentence = available.splice(randomIndex, 1)[0];

      // Check if the selected sentence is an array and transform it if necessary
      if (Array.isArray(selectedSentence) && isSequenced.current) {
        selected.push(...selectedSentence.map(item => item));
      } else {
        selected.push(selectedSentence);
      }
    }

    return selected;
  };

  const markSentenceAsDisplayed = (
    sentence,
    currentModule,
    currentSubModule
  ) => {
    const key = `${currentModule}:${currentSubModule}`;
    if (!displayedSentences.current[key]) {
      displayedSentences.current[key] = [];
    }
    displayedSentences.current[key].push(sentence);
  };

  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  };

  const resetError = () => {
    setError(null);
  };

  return {
    sentences,
    fetchSentences,
    error,
    resetError,
    markSentenceAsDisplayed,
    menuData,
  };
};

export default useSentenceManager;
